import { useCallback, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { useAppStore } from '@/stores/useAppStore';
import { VideoGenerationService } from '@/application/services/VideoGenerationService';
import { AudioGenerationService } from '@/application/services/AudioGenerationService';
import { SceneService } from '@/application/services/SceneService';
import { errorMessages } from '@/lib/errorMessages';
import { logger } from '@/lib/logger';
import { isAbortError } from '@/lib/errorUtils';
import { isVideoGenerationError } from '@/application/errors/VideoGenerationError';

// 動画生成の失敗を、専用の文言を持つものと汎用のものに振り分ける
function videoErrorMessage(error: unknown) {
  if (!isVideoGenerationError(error)) {
    return errorMessages.videoGenerationFailed(error as Error);
  }

  switch (error.code) {
    case 'WEBCODECS_UNSUPPORTED':
      return errorMessages.webCodecsUnsupported();
    case 'AAC_ENCODER_UNAVAILABLE':
      return errorMessages.aacEncoderUnavailable();
  }
}

export function useVideoController() {
  const {
    scenes,
    settings,
    videoSettings,
    subtitleSettings,
    globalSubtitleEnabled,
    updateScene,
    setGenerationStatus,
    resetGeneration,
    showCompletionDialog,
    showErrorConfirmDialog,
    registerCancelCallback,
  } = useAppStore();

  const videoService = useMemo(() => new VideoGenerationService(), []);
  const audioService = useMemo(() => new AudioGenerationService(), []);
  const sceneService = useMemo(() => new SceneService(), []);

  // コンポーネントアンマウント時にWorkerをクリーンアップ
  useEffect(() => {
    return () => {
      audioService.terminateWorker();
    };
  }, [audioService]);

  const handleGenerateVideo = useCallback(
    async (_videoBlob: Blob | null, setVideoBlob: (blob: Blob | null) => void) => {
      const sortedScenes = sceneService.getSortedScenes(scenes);

      const allAudioDone = scenes.length > 0 && scenes.every((s) => s.audioStatus === 'done');
      if (!allAudioDone) {
        toast.error('すべてのシーンに音声を設定してください。');
        return;
      }

      logger.info('動画のみ生成を開始', { sceneCount: sortedScenes.length });

      setGenerationStatus({
        isGenerating: true,
        currentStep: 'video',
        progress: 0,
        currentSceneIndex: 0,
        totalScenes: sortedScenes.length,
        message: '動画生成を開始...',
      });

      // キャンセル処理を登録
      registerCancelCallback(() => {
        videoService.cancelGeneration();
        logger.info('動画生成がユーザーによってキャンセルされました');
      });

      setVideoBlob(null);

      try {
        const { videoBlob, warnings } = await videoService.generateVideo({
          scenes: sortedScenes,
          videoSettings,
          subtitleSettings,
          globalSubtitleEnabled,
          onProgress: (message, progress, warning, currentScene, totalScenes) => {
            setGenerationStatus({
              message,
              progress,
              currentSceneIndex: currentScene,
              totalScenes,
            });
            if (warning) {
              toast.warning(warning);
            }
          },
        });

        // キャンセルコールバックをクリア
        registerCancelCallback(null);

        setVideoBlob(videoBlob);

        setGenerationStatus({
          isGenerating: false,
          currentStep: 'done',
          progress: 100,
          message: '動画生成完了！',
        });
        logger.info('動画生成完了', { sizeKB: Math.round(videoBlob.size / 1024), warningCount: warnings.length });
        resetGeneration();

        const description = warnings.length > 0
          ? `動画の生成が完了しました。\n\n警告 (${warnings.length}件):\n${warnings.join('\n')}`
          : '動画の生成が正常に完了しました。';
        showCompletionDialog('動画生成完了', description);
      } catch (error) {
        // キャンセルコールバックをクリア
        registerCancelCallback(null);

        // AbortErrorの場合はキャンセルとして処理
        if (isAbortError(error)) {
          setGenerationStatus({
            isGenerating: false,
            currentStep: 'error',
            message: '動画生成がキャンセルされました',
          });
          resetGeneration();
          return;
        }

        logger.error('動画生成失敗', error as Error);
        setGenerationStatus({
          isGenerating: false,
          currentStep: 'error',
          message: `動画生成に失敗しました`,
          error: String(error),
        });
        resetGeneration();
        const errMsg = videoErrorMessage(error);
        toast.error(errMsg.title, { description: errMsg.message });
      }
    },
    [scenes, videoSettings, subtitleSettings, globalSubtitleEnabled, sceneService, videoService, setGenerationStatus, resetGeneration, showCompletionDialog, registerCancelCallback]
  );

  const handleGenerateAll = useCallback(
    async (_videoBlob: Blob | null, setVideoBlob: (blob: Blob | null) => void) => {
      if (!settings.elevenLabsApiKey || !settings.voiceId) {
        const errMsg = errorMessages.apiKeyMissing();
        toast.error(errMsg.title, { description: errMsg.message });
        return;
      }

      const sortedScenes = sceneService.getSortedScenes(scenes);
      const scenesToGenerate = audioService.filterScenesForGeneration(sortedScenes);

      if (scenesToGenerate.length === 0) {
        logger.warn('生成するシーンがありません');
        toast.error('生成するシーンがありません', { description: 'すでにすべてのシーンに音声が設定されています。' });
        return;
      }

      logger.info('音声生成を開始（Worker版）', { sceneCount: scenesToGenerate.length });

      // キャンセル処理を登録（Worker版はcancelWorkerGenerationを使用）
      registerCancelCallback(() => {
        audioService.cancelWorkerGeneration();
        videoService.cancelGeneration();
        logger.info('音声+動画生成がユーザーによってキャンセルされました');
      });

      setGenerationStatus({
        isGenerating: true,
        currentStep: 'audio',
        progress: 0,
        currentSceneIndex: 0,
        totalScenes: scenesToGenerate.length,
        message: '音声生成を開始...',
      });

      const audioResult = await audioService.generateBatchAudioWithWorker(
        scenesToGenerate,
        settings,
        {
          onSceneStart: (id) => updateScene(id, { audioStatus: 'generating' }),
          onSceneComplete: (id, data) =>
            updateScene(id, {
              audioStatus: 'done',
              audioUrl: data.audioUrl,
              audioDuration: data.audioDuration,
            }),
          onProgress: (current, total, message) => {
            setGenerationStatus({
              currentSceneIndex: current,
              progress: Math.round((current / total) * 50),
              message,
            });
          },
          onError: async (error, sceneId) => {
            const scene = scenes.find((s) => s.id === sceneId);
            const sceneInfo = scene ? `シーン${scene.order + 1}: ` : '';
            const errMsg = errorMessages.audioGenerationFailed(error);
            return showErrorConfirmDialog(
              errMsg.title,
              `${sceneInfo}${errMsg.message}\n\n残りのシーンの処理を続行しますか？`
            );
          },
        }
      );

      if (!audioResult.success) {
        // キャンセルコールバックをクリア
        registerCancelCallback(null);

        setGenerationStatus({
          isGenerating: false,
          currentStep: 'error',
          message: '音声生成を中断しました',
        });
        resetGeneration();
        return;
      }

      logger.info('全音声生成完了', { totalScenes: scenesToGenerate.length });

      const latestScenes = useAppStore.getState().scenes;
      const scenesForVideo = sceneService.getSortedScenes(latestScenes);

      setGenerationStatus({
        currentStep: 'video',
        progress: 50,
        message: '動画生成を開始...',
      });

      setVideoBlob(null);

      try {
        const { videoBlob, warnings } = await videoService.generateVideo({
          scenes: scenesForVideo,
          videoSettings,
          subtitleSettings,
          globalSubtitleEnabled,
          onProgress: (message, progress, warning, currentScene, totalScenes) => {
            setGenerationStatus({
              message,
              progress: 50 + progress / 2,
              currentSceneIndex: currentScene,
              totalScenes,
            });
            if (warning) {
              toast.warning(warning);
            }
          },
        });

        // キャンセルコールバックをクリア
        registerCancelCallback(null);

        setVideoBlob(videoBlob);

        setGenerationStatus({
          isGenerating: false,
          currentStep: 'done',
          progress: 100,
          message: '音声+動画生成完了！',
        });
        logger.info('音声+動画生成完了', { sizeKB: Math.round(videoBlob.size / 1024), warningCount: warnings.length });
        resetGeneration();

        const description = warnings.length > 0
          ? `音声と動画の生成が完了しました。\n\n警告 (${warnings.length}件):\n${warnings.join('\n')}`
          : '音声と動画の生成が正常に完了しました。';
        showCompletionDialog('動画生成完了', description);
      } catch (error) {
        // キャンセルコールバックをクリア
        registerCancelCallback(null);

        // AbortErrorの場合はキャンセルとして処理
        if (isAbortError(error)) {
          setGenerationStatus({
            isGenerating: false,
            currentStep: 'error',
            message: '動画生成がキャンセルされました',
          });
          resetGeneration();
          return;
        }

        logger.error('動画生成失敗', error as Error);
        setGenerationStatus({
          isGenerating: false,
          currentStep: 'error',
          message: `動画生成に失敗しました`,
          error: String(error),
        });
        resetGeneration();
        const errMsg = videoErrorMessage(error);
        toast.error(errMsg.title, { description: errMsg.message });
      }
    },
    [
      settings,
      scenes,
      videoSettings,
      subtitleSettings,
      globalSubtitleEnabled,
      audioService,
      videoService,
      sceneService,
      updateScene,
      setGenerationStatus,
      resetGeneration,
      showCompletionDialog,
      showErrorConfirmDialog,
      registerCancelCallback,
    ]
  );

  // 共有はタップのハンドラから同期に呼ぶ必要があるため、この関数は async にしない (ADR-0010)
  const handleDeliverVideo = useCallback(
    (videoBlob: Blob | null) => {
      if (!videoBlob) return;
      videoService.deliverVideo(videoBlob);
    },
    [videoService]
  );

  return {
    handleGenerateVideo,
    handleGenerateAll,
    handleDeliverVideo,
  };
}
